"use client"

import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Metric, Bar, fmtCurrency } from "../SmallComponents"
import {
   forecastCalculatorSchema,
   ForecastCalculatorFormValues,
} from "@/utils/helpers/validators/calculators/forecast-calculator.schema"

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import Button from "@/components/ui/Button"

type Result = {
   accuracyGainPoints: number
   annualLossPerStore: number
   annualSavingsPerStore: number
   annualSavingsAllStores: number
   currentError: number
   targetError: number
} | null

export default function ForecastCalculator() {
   const [results, setResults] = useState<Result>(null)

   const form = useForm<ForecastCalculatorFormValues>({
      resolver: zodResolver(forecastCalculatorSchema),
      defaultValues: {
         weeklySales: 40000,
         laborPercent: 30,
         currentAccuracy: 80,
         targetAccuracy: 95,
         locations: 25,
      },
   })

   const currentAccuracy = form.watch("currentAccuracy")
   const targetAccuracy = form.watch("targetAccuracy")
   const locations = form.watch("locations")

   function onSubmit(data: ForecastCalculatorFormValues) {
      const impactFactor = 0.5
      const weeklyLabor = data.weeklySales * (data.laborPercent / 100)

      const currentError = Math.max(0, 100 - data.currentAccuracy) / 100
      const targetError = Math.max(0, 100 - data.targetAccuracy) / 100

      const weeklyWasteNow = weeklyLabor * currentError * impactFactor
      const weeklyWasteAfter = weeklyLabor * targetError * impactFactor
      const weeklySavings = weeklyWasteNow - weeklyWasteAfter

      const annualLossPerStore = weeklyWasteNow * 52
      const annualSavingsPerStore = weeklySavings * 52
      const annualSavingsAllStores = annualSavingsPerStore * data.locations

      const accuracyGainPoints = Math.max(0, data.targetAccuracy - data.currentAccuracy)

      setResults({
         accuracyGainPoints,
         annualLossPerStore,
         annualSavingsPerStore,
         annualSavingsAllStores,
         currentError,
         targetError,
      })
   }

   function reset() {
      form.reset()
      setResults(null)
   }

   const currentWidth = results ? Math.min(results.currentError / 0.4, 1) * 100 : 0
   const targetWidth = results ? Math.min(results.targetError / 0.4, 1) * 100 : 0

   return (
      <div className="scroll-mt-32 rounded-2xl border bg-white p-6 shadow-lg" id="forecast-calculator">
         <div className="space-y-1 p-3 text-center">
            <p className="text-2xl font-bold">Forecast Calculator</p>
            <p className="text-sm text-gray-600">
               Estimate monthly revenue leakage from order errors, peak-hour slowdown, and missed upsells.
            </p>
         </div>
         <div className="mx-auto grid max-w-6xl gap-6 px-3 py-12 md:grid-cols-[60%_40%]">
            {/* INPUTS */}
            <Card className="rounded-2xl shadow-lg">
               <CardHeader>
                  <CardTitle className="text-center text-lg font-semibold">Input your operation</CardTitle>
                  <CardDescription>Fill in a few fields to estimate the cost of inaccurate forecasts.</CardDescription>
                  <div className="h-[2px] w-full rounded-full bg-muted" />
               </CardHeader>
               <CardContent className="space-y-4">
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                     <div>
                        <label className="text-sm text-muted-foreground">Weekly Sales ($)</label>
                        <Input type="number" {...form.register("weeklySales", { valueAsNumber: true })} />
                        <p className="text-xs text-red-500">{form.formState.errors.weeklySales?.message}</p>
                     </div>

                     <div>
                        <label className="text-sm text-muted-foreground">Labor % of Sales</label>
                        <Input type="number" {...form.register("laborPercent", { valueAsNumber: true })} />
                        <p className="text-xs text-red-500">{form.formState.errors.laborPercent?.message}</p>
                     </div>

                     <div>
                        <label className="text-sm text-muted-foreground">Current Forecast Accuracy %</label>
                        <Input type="number" {...form.register("currentAccuracy", { valueAsNumber: true })} />
                        <p className="text-xs text-red-500">{form.formState.errors.currentAccuracy?.message}</p>
                     </div>

                     <div>
                        <label className="text-sm text-muted-foreground">Target Forecast Accuracy %</label>
                        <Input type="number" {...form.register("targetAccuracy", { valueAsNumber: true })} />
                        <p className="text-xs text-red-500">{form.formState.errors.targetAccuracy?.message}</p>
                     </div>

                     <div>
                        <label className="text-sm text-muted-foreground">Locations</label>
                        <Input type="number" {...form.register("locations", { valueAsNumber: true })} />
                        <p className="text-xs text-red-500">{form.formState.errors.locations?.message}</p>
                     </div>

                     <div className="flex gap-3">
                        <Button type="submit" className="flex-1 bg-[#196EB9] text-white hover:bg-[#155a92]">
                           Calculate
                        </Button>
                        <Button
                           type="button"
                           onClick={reset}
                           className="flex-1 bg-[#EB5A23] text-white hover:bg-[#c1481b]"
                        >
                           Reset
                        </Button>
                     </div>
                  </form>
               </CardContent>
            </Card>

            {/* RESULTS */}
            <Card className="rounded-2xl shadow-lg">
               <CardHeader>
                  <CardTitle className="text-center text-lg font-semibold">Impact on Labor & Savings</CardTitle>
                  <CardDescription>Directional estimates based on your forecast accuracy.</CardDescription>
                  <div className="h-[2px] w-full rounded-full bg-muted" />
               </CardHeader>
               <CardContent className="space-y-4">
                  {!results && <p className="text-sm text-muted-foreground">Enter your numbers and click calculate.</p>}

                  {results && (
                     <>
                        <div className="grid gap-4 lg:grid-cols-2">
                           <Metric
                              label="Forecast Improvement"
                              value={`${results.accuracyGainPoints.toFixed(1)} pts`}
                           />
                           <Metric
                              label="Annual Labor Loss (Per Store)"
                              value={`${fmtCurrency(results.annualLossPerStore)}/yr`}
                           />
                           <Metric
                              label="Savings Per Store"
                              value={`${fmtCurrency(results.annualSavingsPerStore)}/yr`}
                           />
                           <Metric label="Total Savings" value={`${fmtCurrency(results.annualSavingsAllStores)}/yr`} />
                        </div>

                        <div className="space-y-3">
                           <Bar
                              label={`Current Forecast Error ${(results.currentError * 100).toFixed(1)}%`}
                              width={currentWidth}
                              color="bg-red-500"
                           />

                           <Bar
                              label={`Target Forecast Error ${(results.targetError * 100).toFixed(1)}%`}
                              width={targetWidth}
                              color="bg-green-500"
                           />
                        </div>

                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                           Improving forecast accuracy from <strong>{currentAccuracy}%</strong> to{" "}
                           <strong>{targetAccuracy}%</strong> could recover roughly{" "}
                           <strong>{fmtCurrency(results.annualSavingsAllStores)}</strong> in labor per year across{" "}
                           <strong>{locations} </strong> locations
                        </div>
                     </>
                  )}
               </CardContent>
            </Card>
         </div>
      </div>
   )
}
